/* eslint-disable no-use-before-define, no-restricted-globals */
// both rules trip on type positions under the babel ESLint parser: the
// interface name referenced above the constructor and its `event` parameter
/**
 * Event object adapter.
 *
 * Adapter object used to abstract events fired from platform. It implements the {@link UIEvent}
 * interface and adapts `event` to it. It also assigns `detail`'s value to its detail
 * property.
 *
 * {@see https://www.w3.org/TR/uievents/#event-interfaces}
 */
export interface PlatformEvent<D = Record<string, any>> {
  // Event interface attributes
  readonly type: string
  readonly target: unknown
  readonly currentTarget: unknown
  readonly eventPhase: number | undefined
  readonly bubbles: boolean | undefined
  readonly cancelable: boolean | undefined
  readonly composed: boolean | undefined
  readonly timeStamp: number | undefined
  readonly defaultPrevented: boolean | undefined
  readonly isTrusted: boolean | undefined
  // Additional attributes
  /** holds the data being dragged during drag and drop */
  readonly dataTransfer: unknown
  /** `detail`'s contents */
  readonly detail: D
  /** the event triggered from the native platform */
  readonly nativeEvent: unknown
  readonly key: string | undefined
  // Event interface methods
  stopPropagation: () => void
  stopImmediatePropagation: () => void
  preventDefault: () => void
  initEvent: () => void
  // Additional methods
  /** whether {@link stopImmediatePropagation} was executed */
  isPropagationStopped: () => boolean | undefined
  /** whether {@link preventDefault} was executed */
  isDefaultPrevented: () => boolean | undefined
  /** holds the event onto a reference, preventing it from being added back
   to the React's SyntheticEvent pool */
  persist: () => void
}

/**
 * Creates a new {@link PlatformEvent} from the `event` that originated it, or
 * from the type of the new event, optionally carrying `detail` as payload.
 */
export interface PlatformEventConstructor {
  new <D = Record<string, any>>(event: unknown, detail?: D | null): PlatformEvent<D>
}

/**
 * Event Object Constructor
 *
 * Accepts `event` and creates a new `PlatformEvent` instance based on it.
 */
export const PlatformEvent = function PlatformEvent(this: any, event: any, detail: any = null) {
  const rootEvent = typeof event === 'object' && event ? event : { type: event || '' }
  const eventDetail = detail || rootEvent.detail || {}

  Object.defineProperties(this, {
    // Event interface attributes
    type: {
      get: () => rootEvent.type,
      enumerable: true,
    },
    target: {
      get: () => rootEvent.target,
      enumerable: true,
    },
    currentTarget: {
      get: () => rootEvent.currentTarget,
      enumerable: true,
    },
    eventPhase: {
      get: () => rootEvent.eventPhase,
      enumerable: true,
    },
    bubbles: {
      get: () => rootEvent.bubbles,
      enumerable: true,
    },
    cancelable: {
      get: () => rootEvent.cancelable,
      enumerable: true,
    },
    composed: {
      get: () => rootEvent.composed,
      enumerable: true,
    },
    timeStamp: {
      get: () => rootEvent.timeStamp,
      enumerable: true,
    },
    defaultPrevented: {
      get: () => rootEvent.defaultPrevented,
      enumerable: true,
    },
    isTrusted: {
      get: () => rootEvent.isTrusted,
      enumerable: true,
    },
    // Additional attributes
    dataTransfer: {
      get: () => rootEvent.dataTransfer,
      enumerable: true,
    },
    detail: {
      get: () => eventDetail,
      enumerable: true,
    },
    nativeEvent: {
      get: () => rootEvent.nativeEvent,
      enumerable: true,
    },
    // Event interface methods
    stopPropagation: {
      value: () => (PLATFORM === 'web'
        ? rootEvent.stopPropagation?.()
        : rootEvent.persist?.()),
      enumerable: true,
    },
    stopImmediatePropagation: {
      value: () => rootEvent.stopImmediatePropagation?.(),
      enumerable: true,
    },
    preventDefault: {
      value: () => (PLATFORM === 'web'
        ? rootEvent.preventDefault?.()
        : rootEvent.persist?.()),
      enumerable: true,
    },
    initEvent: {
      value: () => rootEvent.initEvent?.(),
      enumerable: true,
    },
    // Additional methods
    isPropagationStopped: {
      value: () => rootEvent.isPropagationStopped?.(),
      enumerable: true,
    },
    isDefaultPrevented: {
      value: () => rootEvent.isDefaultPrevented?.(),
      enumerable: true,
    },
    persist: {
      value: () => rootEvent.persist?.(),
      enumerable: true,
    },
    key: {
      get: () => rootEvent?.key,
      enumerable: true,
    },
  })
} as unknown as PlatformEventConstructor

export default PlatformEvent
